// Hilfsfunktionen rund um die Anrufzeiten einer Praxis.
//
// Zeitzonenstrategie: Berechnungen laufen immer in Europe/Berlin, unabhängig
// von der lokalen Zeitzone des Browsers. Damit zeigt die Ampel auf einem im
// Ausland geöffneten Laptop dieselbe Aussage wie für den Vertriebler vor Ort.

export const ANRUF_TAGE = [
  { value: 'mo', label: 'Mo' },
  { value: 'di', label: 'Di' },
  { value: 'mi', label: 'Mi' },
  { value: 'do', label: 'Do' },
  { value: 'fr', label: 'Fr' },
  { value: 'sa', label: 'Sa' },
];

// 30-Minuten-Raster für die Anrufzeit-Dropdowns im Praxis-Formular.
// Von 07:00 bis 20:00 inklusive — alles davor/danach passt im Vertriebsalltag
// nicht zur typischen Praxis-Öffnungszeit und würde den Select unnötig lang
// machen.
export const ANRUFZEIT_RASTER = (() => {
  const out = [];
  for (let h = 7; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 20 && m > 0) break;
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

const ANRUFZEIT_SET = new Set(ANRUFZEIT_RASTER);

/**
 * Baut die Optionsliste für ein Anrufzeit-Dropdown. Erste Option ist immer
 * "— nicht gesetzt —" (Leerstring); danach das 30-Minuten-Raster. Liegt der
 * aktuelle Wert außerhalb des Rasters (z. B. 08:15 aus früherer Freitext-
 * Eingabe), wird er als zusätzliche Option mit Suffix "(bisheriger Wert)"
 * angehängt, damit Altbestand nicht still verschwindet.
 */
export function buildAnrufzeitOptions(currentValue) {
  const trimmed = (currentValue ?? '').trim();
  const optionen = [
    { value: '', label: '— nicht gesetzt —' },
    ...ANRUFZEIT_RASTER.map((t) => ({ value: t, label: t })),
  ];
  if (trimmed && !ANRUFZEIT_SET.has(trimmed)) {
    optionen.push({ value: trimmed, label: `${trimmed} (bisheriger Wert)` });
  }
  return optionen;
}

export const ANRUF_TAG_LANG = {
  mo: 'Montag',
  di: 'Dienstag',
  mi: 'Mittwoch',
  do: 'Donnerstag',
  fr: 'Freitag',
  sa: 'Samstag',
};

const TZ = 'Europe/Berlin';

const WEEKDAY_MAP = {
  Mon: 'mo',
  Tue: 'di',
  Wed: 'mi',
  Thu: 'do',
  Fri: 'fr',
  Sat: 'sa',
  Sun: 'so',
};

// Liefert Wochentag (mo–so) und Minuten seit Mitternacht in Europe/Berlin.
function berlinNow(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = fmt.formatToParts(date);
  const map = {};
  for (const p of parts) map[p.type] = p.value;
  const weekday = WEEKDAY_MAP[map.weekday] ?? null;
  const hours = Number(map.hour);
  const minutes = Number(map.minute);
  return { weekday, minutes: hours * 60 + minutes };
}

function parseHHMM(value) {
  if (!value || typeof value !== 'string') return null;
  const m = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function formatHHMM(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Berechnet den Anruf-Status einer Praxis zum übergebenen Zeitpunkt.
 *
 * Rückgabewerte:
 *   level: 'gruen' | 'gelb' | 'rot' | 'grau'
 *   text:  beschreibender Text für die UI
 *
 * 'gelb' bedeutet "in höchstens 30 Minuten beginnt ein Fenster".
 * 'grau' bedeutet "es liegen gar keine Anrufzeiten vor".
 */
export function anrufZustand(praxis, now = new Date()) {
  if (!praxis) return { level: 'grau', text: 'Keine Anrufzeiten hinterlegt' };

  const vmVon = parseHHMM(praxis.anruf_vm_von);
  const vmBis = parseHHMM(praxis.anruf_vm_bis);
  const nmVon = parseHHMM(praxis.anruf_nm_von);
  const nmBis = parseHHMM(praxis.anruf_nm_bis);

  const hatVm = vmVon !== null && vmBis !== null && vmBis > vmVon;
  const hatNm = nmVon !== null && nmBis !== null && nmBis > nmVon;

  const keinVm = asArray(praxis.anruf_kein_vm);
  const keinNm = asArray(praxis.anruf_kein_nm);
  const geschlossen = asArray(praxis.anruf_geschlossen);

  const garNichts =
    !hatVm &&
    !hatNm &&
    keinVm.length === 0 &&
    keinNm.length === 0 &&
    geschlossen.length === 0;
  if (garNichts) return { level: 'grau', text: 'Keine Anrufzeiten hinterlegt' };

  const { weekday, minutes } = berlinNow(now);

  if (geschlossen.includes(weekday)) {
    return { level: 'rot', text: 'Heute geschlossen' };
  }

  const heuteVm = hatVm && !keinVm.includes(weekday);
  const heuteNm = hatNm && !keinNm.includes(weekday);

  if (!heuteVm && !heuteNm) {
    return { level: 'rot', text: 'Heute keine Anrufzeit' };
  }

  // Tagesfenster in Zeit-Reihenfolge.
  const fenster = [];
  if (heuteVm) fenster.push({ von: vmVon, bis: vmBis, slot: 'vm' });
  if (heuteNm) fenster.push({ von: nmVon, bis: nmBis, slot: 'nm' });
  fenster.sort((a, b) => a.von - b.von);

  // Aktuell innerhalb eines Fensters?
  for (const f of fenster) {
    if (minutes >= f.von && minutes < f.bis) {
      return { level: 'gruen', text: `Jetzt erreichbar bis ${formatHHMM(f.bis)}` };
    }
  }

  // Nächstes Fenster heute?
  const naechstes = fenster.find((f) => minutes < f.von);
  if (naechstes) {
    const diff = naechstes.von - minutes;
    const level = diff <= 30 ? 'gelb' : 'rot';
    // Wenn ein Fenster bereits vorbei ist, sind wir effektiv in der Mittagspause.
    const vorherSchonOffen = fenster.some((f) => minutes >= f.bis);
    const text = vorherSchonOffen
      ? `Mittagspause bis ${formatHHMM(naechstes.von)}`
      : `Öffnet um ${formatHHMM(naechstes.von)}`;
    return { level, text };
  }

  return { level: 'rot', text: 'Anrufzeit vorbei' };
}
